import React from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, ExternalLink } from 'lucide-react'
import { DASHBOARD_MAIN_NAV } from '../../lib/dashboardNav'
import { BandHeading } from './OverviewPanel'

/**
 * Build chip labels for a main nav module from overview data.
 * @param {string} id
 * @param {object} data
 * @returns {string[]}
 */
function chipsForModule(id, data) {
  switch (id) {
    case 'clients':
      if (!data.clientStats) return []
      return [`${data.clientStats.active} active`, `${data.clientStats.total} total`]
    case 'analytics': {
      // Do not show total budget from clientStats (that requires Clients access).
      // Prefer company-split summary when analytics payload is present.
      if (data.companySplit && typeof data.companySplit === 'object') {
        const n = Object.keys(data.companySplit).length
        if (n > 0) return [`${n} companies`]
      }
      return []
    }
    case 'adimpact-alerts':
      if (data.alertCount != null) return [`${data.alertCount} alerts`]
      return []
    case 'resources':
      if (data.resourceCount != null) return [`${data.resourceCount} links`]
      return []
    case 'race-tracker':
      if (!data.raceStats) return []
      return [
        `${data.raceStats.activeRaces} races`,
        `${data.raceStats.digitalOverdue + data.raceStats.linearOverdue} overdue`,
      ]
    case 'big-political-list':
      if (!data.bplStats) return []
      return [`${data.bplStats.stations} stations`]
    case 'idea-board':
      if (!data.ideaBoardStats) return []
      return [`${data.ideaBoardStats.openSuggestions} open`]
    default:
      return []
  }
}

/**
 * @param {string} id
 */
function hrefForModule(id) {
  if (id === 'race-tracker') return '/race-tracker'
  return `/?tab=${encodeURIComponent(id)}`
}

/**
 * Main workspace module tiles.
 */
export default function MainWorkspaceGrid({ canAccessPage, data, loading }) {
  const tiles = DASHBOARD_MAIN_NAV.filter((n) => !n.hidden && canAccessPage(n.id))
  if (!tiles.length) return null

  return (
    <section className="overview-band overview-fade-in" aria-label="Main">
      <BandHeading title="Main" subtitle="Core workspace modules you can open" />
      <div className="overview-tile-grid">
        {tiles.map(({ id, label, Icon }) => {
          const chips = chipsForModule(id, data)
          const href = hrefForModule(id)
          return (
            <Link key={id} to={href} className="overview-module-tile">
              <div className="overview-module-tile-icon" aria-hidden>
                {Icon ? <Icon size={20} strokeWidth={1.75} /> : null}
              </div>
              <div className="overview-module-tile-body">
                <h3 className="overview-module-tile-title">{label.trim()}</h3>
                <div className="overview-module-tile-chips">
                  {chips.length
                    ? chips.map((c) => (
                        <span key={c} className="overview-chip overview-chip--quiet">
                          {c}
                        </span>
                      ))
                    : loading
                      ? (
                          <span className="overview-muted-copy">…</span>
                        )
                      : (
                          <span className="overview-muted-copy">Open module</span>
                        )}
                </div>
              </div>
              <span className="overview-module-tile-open">
                Open
                <ChevronRight size={14} aria-hidden />
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

/**
 * External internal apps row.
 */
export function InternalAppsRow({ canAccessPage, items }) {
  const tiles = items.filter((n) => canAccessPage(n.id) && n.href)
  if (!tiles.length) return null

  return (
    <section className="overview-band overview-fade-in" aria-label="Internal Apps">
      <BandHeading title="Internal Apps" subtitle="SMG tools outside this dashboard" />
      <div className="overview-apps-row">
        {tiles.map(({ id, label, href }) => (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="overview-app-tile"
          >
            <span>{label}</span>
            <ExternalLink size={14} aria-hidden />
          </a>
        ))}
      </div>
    </section>
  )
}
